import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import {
    initializeChecker,
    openStep,
    openCde,
    updateStep,
    updateCde,
    deleteStep,
    updateFinalAnswer,
    deleteCde,
    closeAll,
    addCde,
    addStep,
    validate,
    ackSync,
} from '../actions/checker';
import { getCheckerQuestion, getMathContent, isExerciseWithCdes, VALIDATION_STATUSES } from '../lib/checker';

const initialState = {
    exerciseId: undefined,
    openedCde: undefined,
    openedStep: undefined,
    cdes: [],
    steps: [],
    finalAnswer: undefined,
    isSynced: true,
    isValidated: true,
};

export const reducer = createReducer(initialState, (builder) => {
    builder.addCase(initializeChecker, (state, action) => {
        const { answers, exercise } = action.payload;

        const hasCdes = isExerciseWithCdes(exercise);
        const defaultMath = getCheckerQuestion(exercise.question);

        if (isEmpty(answers)) {
            state.exerciseId = exercise.id;
            state.steps = [];
            state.cdes = [];
            state.finalAnswer = undefined;

            state.openedStep = !hasCdes ? 0 : undefined;
            state.steps.push({
                value: defaultMath,
            });

            if (hasCdes) {
                state.openedCde = 0;
                state.cdes.push({
                    value: '',
                });
            }
        } else {
            state.exerciseId = answers.exerciseId;
            state.steps = answers.steps;
            state.cdes = answers.cdes;
            state.openedCde = answers.openedCde;
            state.openedStep = answers.openedStep;
            state.finalAnswer = answers.finalAnswer;
        }
    });
    builder.addCase(openStep, (state, action) => {
        const { index } = action.payload;
        state.openedStep = index;
    });
    builder.addCase(openCde, (state, action) => {
        const { index } = action.payload;
        state.openedCde = index;
    });
    builder.addCase(updateStep, (state, action) => {
        const { index, value } = action.payload;
        state.steps[index].value = value;
    });
    builder.addCase(updateCde, (state, action) => {
        const { index, value } = action.payload;
        state.cdes[index].value = value;
    });
    builder.addCase(deleteStep, (state, action) => {
        const { index } = action.payload;
        state.steps.splice(index, 1);
        state.openedStep = undefined;
    });
    builder.addCase(deleteCde, (state, action) => {
        const { index } = action.payload;
        state.cdes.splice(index, 1);
        state.openedCde = undefined;
    });
    builder.addCase(updateFinalAnswer, (state, action) => {
        const { value } = action.payload;
        if (state.finalAnswer === value) {
            state.finalAnswer = undefined;
        } else {
            state.finalAnswer = value;
        }
    });
    builder.addCase(closeAll, (state) => {
        const canClearCdes = state.cdes.some((cde) => getMathContent(cde.value));
        if (canClearCdes) {
            state.openedCde = undefined;
            state.cdes = state.cdes.filter((cde) => getMathContent(cde.value));
        }

        state.openedStep = undefined;
    });
    builder.addCase(addCde.fulfilled, (state, action) => {
        const { validatedCdes, index } = action.payload;
        state.cdes = validatedCdes;

        state.cdes.push({ value: '' });
        state.openedCde = index + 1;
    });
    builder.addCase(addStep.fulfilled, (state, action) => {
        const { validatedSteps, index, isCorrectable } = action.payload;
        state.steps = validatedSteps;

        const isStepWrong = validatedSteps[index].validationStatus === VALIDATION_STATUSES.ERROR;
        if ((isCorrectable && !isStepWrong) || !isCorrectable) {
            const previousStep = state.steps[index];
            state.steps.splice(index + 1, 0, {
                value: previousStep.value,
                validationStatus: previousStep.validationStatus,
            });
        }
        state.openedStep = index + 1;
    });
    builder.addCase(validate.pending, (state) => {
        const setPending = (item) => ({ ...item, validationStatus: VALIDATION_STATUSES.PENDING });
        state.steps = state.steps.map(setPending);
        state.cdes = state.cdes.map(setPending);
    });
    builder.addCase(validate.fulfilled, (state, action) => {
        const { validatedSteps, validatedCdes } = action.payload;

        state.steps = validatedSteps;
        state.cdes = validatedCdes;
        state.isValidated = true;
    });
    builder.addCase(ackSync, (state) => {
        state.isSynced = true;
    });
    builder.addMatcher(
        isAnyOf(validate.fulfilled, addStep.fulfilled, addCde.fulfilled, updateStep, updateCde, updateFinalAnswer, deleteStep, deleteCde),
        (state) => {
            state.isSynced = false;
        },
    );
    builder.addMatcher(isAnyOf(updateStep, updateCde, updateFinalAnswer, deleteStep, deleteCde), (state) => {
        state.isValidated = false;
    });
});
